/**
 * BuilderMark — the Builder logomark as a pure SVG.
 *
 * Designed to read cleanly at 16px (favicon territory) all the way up to
 * 96px+ (auth splash). Uses `currentColor` so the caller controls the ink
 * via Tailwind — which makes dark mode free and lets the TopBar keep the
 * mark in `text-ink` while the auth hero can promote it to `text-brand`.
 *
 * Geometry: a single continuous stroke that traces walls → roof → walls →
 * floor → door → floor, so the whole thing reads as one hand-drawn line in
 * the spirit of the original sketch, without the busyness the raster had.
 *
 * A secondary, softer roofline sits behind the main peak to echo the
 * double-roof feel of the original artwork.
 *
 * strokeWidth 1.7 at viewBox 24 is the sweet spot: visible at 16px without
 * feeling clunky, and still airy at 64px+. `round` caps/joins soften the
 * corners so the mark doesn't feel like a stock CAD icon.
 */
interface BuilderMarkProps {
  className?: string;
  /**
   * Optional accessible label. When omitted the SVG is marked aria-hidden,
   * which is what you want when the mark sits next to a visible wordmark.
   */
  title?: string;
}

export function BuilderMark({ className, title }: BuilderMarkProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={className}
    >
      {/* Echo roofline — subtle second peak behind the main roof.
          Thinner stroke + lower opacity so it reads as shadow/recall,
          not as a competing shape. */}
      <path d="M2 13.5 L12 4 L22 13.5" opacity={0.4} strokeWidth={1.35} />

      {/* Main house: a single continuous path — left wall up, roof peak,
          right wall down, floor to right door jamb, up to door header,
          across to left jamb, back down to floor, close to start. */}
      <path
        d="M3.5 21 L3.5 12 L12 5 L20.5 12 L20.5 21 L14 21 L14 15 L10 15 L10 21 Z"
      />
    </svg>
  );
}
